if (!customElements.get('product-tab-banners')) {
  class ProductTabBanners extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('[data-product-tab-banners-nav]');
      this.navTrack = this.querySelector('[data-product-tab-banners-nav-track]');
      this.spacer = this.querySelector('[data-product-tab-banners-spacer]');
      this.banner = this.querySelector('[data-product-tab-banners-banner]');
      this.container = this.closest('.product-tab-banners') || this;
      this.mainContent = document.getElementById('main-content');
      this.links = Array.from(this.querySelectorAll('[data-product-tab-banners-link]'));
      this.activeLinks = [];
      this.fixedLeft = null;
      this.fixedWidth = null;
      this.isSticky = false;
      this.navHeight = null;
      this.pageOffset = null;
      this.stickyTop = null;
      this.updateQueued = false;

      if (!this.nav || !this.links.length) {
        return;
      }

      this.links.forEach((link) => {
        link.addEventListener('click', () => {
          const target = this.getTarget(link);

          if (target) {
            this.scrollToTarget(target);
            this.setActiveLink(link);
          }
        });
      });

      this.boundOnScroll = this.onScroll.bind(this);
      this.boundOnResize = this.onResize.bind(this);
      window.addEventListener('scroll', this.boundOnScroll, { passive: true });
      window.addEventListener('resize', this.boundOnResize);
      window.addEventListener('load', this.boundOnResize);

      this.observer = new MutationObserver(() => this.queueUpdate());
      this.observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      this.refresh();
      window.setTimeout(() => this.refresh(), 600);
      window.setTimeout(() => this.refresh(), 1600);
      window.setTimeout(() => this.refresh(), 3200);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.boundOnScroll);
      window.removeEventListener('resize', this.boundOnResize);
      window.removeEventListener('load', this.boundOnResize);
      this.observer?.disconnect();
      this.clearPageOffset();
    }

    queueUpdate() {
      if (this.updateQueued) {
        return;
      }

      this.updateQueued = true;
      window.requestAnimationFrame(() => {
        this.updateQueued = false;
        this.refresh();
      });
    }

    refresh() {
      this.updateTargets();
      this.updateMeasurements();
      this.updateStickyState();
      this.updateActiveFromScroll();
    }

    updateTargets() {
      this.activeLinks = [];

      this.links.forEach((link) => {
        const target = this.getTarget(link);
        const isVisible = this.isValidTarget(target);
        link.classList.toggle('is-hidden', !isVisible);
        link.disabled = !isVisible;

        if (isVisible) {
          this.activeLinks.push(link);
        }
      });

      const hasVisibleLink = this.activeLinks.length > 0;
      this.nav.classList.toggle('is-empty', !hasVisibleLink);
      this.container.classList.toggle('is-empty', !hasVisibleLink && !this.banner);

      if (hasVisibleLink && !this.activeLinks.some((link) => link.classList.contains('is-active'))) {
        this.setActiveLink(this.activeLinks[0]);
      }
    }

    getTarget(link) {
      if (link.hasAttribute('data-target-self')) {
        return this.banner || this;
      }

      const selector = link.dataset.targetSelector;

      if (!selector) {
        return null;
      }

      if (link.dataset.targetKey === 'reviews') {
        return this.getReviewsTarget(selector);
      }

      return document.querySelector(selector);
    }

    getReviewsTarget(selector) {
      const sections = Array.from(document.querySelectorAll(selector));

      return sections.find((section) => {
        const markup = section.innerHTML.toLowerCase();

        return markup.includes('loox') || section.querySelector('[class*="loox"], [id*="loox"], iframe[src*="loox"]');
      }) || null;
    }

    isValidTarget(target) {
      if (!target) {
        return false;
      }

      if (target === this.banner) {
        return target.getBoundingClientRect().height > 1;
      }

      const rect = target.getBoundingClientRect();
      return rect.height > 1 && rect.width > 1;
    }

    scrollToTarget(target) {
      const targetTop = window.scrollY + target.getBoundingClientRect().top;
      const offset = this.getScrollStickyTop(targetTop) + this.getNavHeight();
      const top = Math.max(targetTop - offset + 1, 0);

      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }

    updateMeasurements() {
      const wasFixed = this.classList.contains('is-fixed');
      this.classList.remove('is-fixed');

      if (this.spacer) {
        this.spacer.hidden = true;
      }

      const navRect = this.nav.getBoundingClientRect();
      this.style.setProperty('--product-tab-banners-nav-height', `${navRect.height}px`);
      this.updateFixedBounds();

      if (wasFixed) {
        this.classList.add('is-fixed');
        if (this.spacer) {
          this.spacer.hidden = false;
        }
      }
    }

    updateFixedBounds() {
      const rect = this.getBoundingClientRect();
      this.style.setProperty('--product-tab-banners-fixed-left', `${rect.left}px`);
      this.style.setProperty('--product-tab-banners-fixed-width', `${rect.width}px`);
    }



    updateStickyState() {
      const shouldStick = this.activeLinks.length > 0;

      this.classList.toggle('is-fixed', shouldStick);
      this.updatePageOffset(shouldStick);

      if (this.spacer) {
        this.spacer.hidden = !shouldStick;
      }

      this.style.setProperty('--product-tab-banners-sticky-top', `${this.getStickyTop()}px`);

      if (shouldStick) {
        this.updateFixedBounds();
      }
    }

    getStickyTop() {
      const headerSection = document.querySelector('.header-section');

      if (!headerSection || headerSection.classList.contains('header-section--hidden')) {
        return 0;
      }

      return Math.max(headerSection.getBoundingClientRect().bottom, 0);
    }

    getScrollStickyTop(targetTop) {
      if (window.scrollY > 1 || targetTop <= window.scrollY) {
        return this.getStickyTop();
      }

      const headerSection = document.querySelector('.header-section');

      if (!headerSection) {
        return 0;
      }

      const position = window.getComputedStyle(headerSection).position;

      if (position !== 'sticky' && position !== 'fixed') {
        return 0;
      }

      return Math.max(headerSection.getBoundingClientRect().height, 0);
    }

    updatePageOffset(shouldStick) {
      if (!this.mainContent) {
        return;
      }

      const offset = shouldStick ? this.getNavHeight() : 0;
      document.documentElement.style.setProperty('--product-tab-banners-page-offset', `${offset}px`);
      document.body.classList.toggle('product-tab-banners-fixed', shouldStick);
    }

    clearPageOffset() {
      document.body.classList.remove('product-tab-banners-fixed');
      document.documentElement.style.removeProperty('--product-tab-banners-page-offset');
    }

    getNavHeight() {
      return this.nav.getBoundingClientRect().height || 0;
    }

    updateActiveFromScroll() {
      if (!this.activeLinks.length) {
        return;
      }

      const offset = this.getStickyTop() + this.getNavHeight() + 8;
      const currentY = window.scrollY + offset;
      let activeLink = this.activeLinks[0];

      this.activeLinks.forEach((link) => {
        const target = this.getTarget(link);

        if (!target) {
          return;
        }

        const targetTop = window.scrollY + target.getBoundingClientRect().top;

        if (targetTop <= currentY) {
          activeLink = link;
        }
      });

      this.setActiveLink(activeLink);
    }

    setActiveLink(activeLink) {
      this.links.forEach((link) => {
        link.classList.toggle('is-active', link === activeLink);
      });
    }

    onScroll() {
      this.updateStickyState();
      this.updateActiveFromScroll();
    }

    onResize() {
      this.refresh();
    }
  }

  customElements.define('product-tab-banners', ProductTabBanners);
}
